/**
 * <s-product-thumbnails> — Mobile thumbnail strip for the product gallery.
 *
 * Thumbnail buttons use the Invoker Commands API (`commandfor` + `command="--show-media"`)
 * targeting the gallery; this component listens for that command on the gallery, scrolls
 * the gallery to the requested slide, and keeps `aria-current` in sync as buyers swipe.
 *
 * Required markup:
 *   [data-gallery]                     — id of the horizontally scrolling gallery element.
 *   button[command="--show-media"]     — one per slide; `value` is the zero-based slide index.
 *   [data-thumb-strip]                 — scrollable row that holds the thumbnail buttons.
 */
class SProductThumbnails extends HTMLElement {
  #ac;
  #gallery;
  #frame = 0;

  connectedCallback() {
    this.#gallery = document.getElementById(this.dataset.gallery);
    if (!this.#gallery) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#gallery.addEventListener('command', this.#handleCommand, opts);
    this.#gallery.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #buttons() {
    return [...this.querySelectorAll('button[command="--show-media"]')];
  }

  #handleCommand = (event) => {
    if (event.command !== '--show-media') return;
    const index = Number(event.source?.value);
    const slide = this.#gallery.children[index];
    if (!slide) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const offset = slide.getBoundingClientRect().left - this.#gallery.getBoundingClientRect().left;
    this.#gallery.scrollBy({ left: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setActive(index);
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.#gallery.clientWidth;
      if (!width) return;
      this.#setActive(Math.round(this.#gallery.scrollLeft / width));
    });
  };

  #setActive(index) {
    const buttons = this.#buttons;
    buttons.forEach((button, i) => {
      if (i === index) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });

    const strip = this.querySelector('[data-thumb-strip]');
    const active = buttons[index];
    if (!strip || !active) return;
    const stripRect = strip.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    if (activeRect.left < stripRect.left || activeRect.right > stripRect.right) {
      strip.scrollBy({ left: activeRect.left - stripRect.left - (stripRect.width - activeRect.width) / 2 });
    }
  }
}

if (!customElements.get('s-product-thumbnails')) {
  customElements.define('s-product-thumbnails', SProductThumbnails);
}
